import { useState, useEffect } from "react";
import { getLLMKeysStatus, getPlatformConfig, setPlatformConfig, getAntiAbuseConfig, updateAntiAbuseConfig, getTrialFunnelStatus, setTrialFunnelEnabled } from "../lib/api";
import { DiagramContent, type DiagramTab } from "./SystemInfoDiagrams";
import { SettingsTabs } from "./SystemInfoSettings";
import TrialWalkthrough from "./TrialWalkthrough";
import CoreKbPanel from "./CoreKbPanel";
import SystemInfoEmployees from "./SystemInfoEmployees";

/** TAB 分三組（2026-09-14 重整）：⚙️ 設定｜📚 說明｜📋 日誌 */
const TAB_GROUPS: { group: string; tabs: { id: DiagramTab; label: string; hint: string }[] }[] = [
  {
    group: "⚙️ 設定",
    tabs: [
      { id: "brand", label: "🏷️ 品牌設定", hint: "AI 回覆署名：平台預設內容＋各租戶開關狀態（租戶層在「租戶編輯頁」設定）；改完約 1 分鐘生效" },
      { id: "llm", label: "🧠 LLM 與模型", hint: "引擎層級（Premium/Basic）、系統預設 API Key 狀態、模型對照與更新指引" },
      { id: "abuse", label: "🛡️ 對話上限", hint: "非誠勿擾：session 上限（Lite 10／Pro 15，閒置重置 30 分鐘）＋跨號碼 burst limit（Lite 15min/20／Pro 15min/50）；admin 可改" },
      { id: "corekb", label: "🌀 Core KB 蒸餾", hint: "平台級答法層：每週三 off-peak 全平台對話蒸餾（≥3 租戶共現）→審批上架→檢索梯第二層——越用越聰明＋越用越平（2026-10-10 新增）" },
      { id: "rich", label: "🎛️ 互動訊息", hint: "CloudWAPI 按鈕／選單／圖片增益通道：功能旗現況・AI 標記協議・平台地雷一頁睇（2026-09-19 收編 rich-media.ts 後新增）" },
      { id: "trial", label: "🧪 試用漏斗", hint: "線 2 試用轉化：平台總開關（領取入口＋定時任務）＋30 池現況／排隊／轉化即時數（2026-09-23 新增）" },
      { id: "trialwalk", label: "🧭 試用之路", hint: "分幕式講解：試用漏斗整條路 60 秒行完——第一次接觸都瞬間明白（新形式實驗）" },
    ],
  },
  {
    group: "📚 說明",
    tabs: [
      { id: "sys", label: "🏠 概覽", hint: "系統端點、部署版本、API 參考" },
      { id: "cheatsheet", label: "⚡ 速查", hint: "一頁睇晒：訊息一生・入口 URL・開關・監控・故障排查・應急操作" },
      { id: "overview", label: "🏗️ 整體架構", hint: "全系統分層總覽" },
      { id: "employees", label: "🧬 AI 員工", hint: "Employee Pack、facade／Skill Pack、執行模式與 Google Calendar 自助連接" },
      { id: "prompt", label: "🧩 Prompt 組裝", hint: "每則回覆點樣砌出嚟：鐵律・人格・軟技能・知識四層分工" },
      { id: "chat", label: "💬 對話與轉真人", hint: "訊息流・節奏引擎・智能分流・人工接管" },
      { id: "learning", label: "🧠 自我學習迴圈", hint: "真人回覆沉澱為知識庫（越用越聰明）" },
      { id: "onboard", label: "🧙 開通漏斗", hint: "線 1 付款開通全鏈（KPay 收銀台→待辦→歡迎包→門戶三步）＋師傅 15 分鐘設定・一鍵安裝" },
    ],
  },
  {
    group: "📋 日誌",
    tabs: [
      { id: "syslog", label: "🚨 系統事件", hint: "自動採集：cron 失敗／出站發送失敗／AI 硬失敗（30 日保留）——排錯第一步睇呢度" },
      { id: "log", label: "📋 更新日誌", hint: "8/27 起全部系統更新——系統唯一日誌入口" },
    ],
  },
];

const DIAGRAM_TABS = TAB_GROUPS.flatMap((g) => g.tabs);

export default function SystemInfoPage() {
  const [tab, setTab] = useState<DiagramTab>(() => {
    const requested = new URLSearchParams(window.location.search).get("tab");
    return DIAGRAM_TABS.some((item) => item.id === requested) ? requested as DiagramTab : "sys";
  });
  const [llmKeys, setLlmKeys] = useState<{ deepseek?: { configured: boolean; masked: string | null }; zhipu?: { configured: boolean; masked: string | null } } | null>(null);
  const [llmKeysLoading, setLlmKeysLoading] = useState(true);
  // 品牌署名（platform_config brand_footer）平台預設值
  const [brandValue, setBrandValue] = useState("");
  const [brandLoading, setBrandLoading] = useState(true);
  const [brandSaving, setBrandSaving] = useState(false);
  const [brandMsg, setBrandMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // 署名狀態一覽分頁（Captain 2026-09-12：list 一律每頁 10 筆）
  // 🛡️ 對話上限（非誠勿擾）——platform_config anti_abuse_*，預設 Lite 10／Pro 15／重置 30 分鐘；跨號碼 burst Lite 20／Pro 50／窗口 15 分鐘
  const [abuseForm, setAbuseForm] = useState({ lite: 10, pro: 15, resetMin: 30, burstLite: 20, burstPro: 50, burstWindowMin: 15, burstEnabled: false });
  const [abuseLoading, setAbuseLoading] = useState(true);
  const [abuseSaving, setAbuseSaving] = useState(false);
  const [abuseMsg, setAbuseMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // 🧪 試用漏斗（platform_config trial_funnel_enabled＋/admin/trial/report）
  interface TrialReportData { ok?: boolean; [k: string]: unknown }
  const [trialEnabled, setTrialEnabled] = useState(true);
  const [trialReport, setTrialReport] = useState<TrialReportData | null>(null);
  const [trialLoading, setTrialLoading] = useState(true);
  const [trialSaving, setTrialSaving] = useState(false);
  const [trialMsg, setTrialMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    getLLMKeysStatus()
      .then((data) => {
        if (data.success && data.keys) {
          setLlmKeys(data.keys);
        }
      })
      .catch((err) => console.error("Failed to load LLM keys status:", err))
      .finally(() => setLlmKeysLoading(false));
    getPlatformConfig("brand_footer")
      .then((data) => {
        if (data.exists) setBrandValue(data.value || "");
      })
      .catch((err) => console.error("Failed to load brand footer:", err))
      .finally(() => setBrandLoading(false));
    getAntiAbuseConfig()
      .then((data) => {
        if (data.success && data.defaults) setAbuseForm(data.defaults);
      })
      .catch((err) => console.error("Failed to load anti-abuse config:", err))
      .finally(() => setAbuseLoading(false));
    getTrialFunnelStatus()
      .then((data) => {
        if (typeof data.enabled === "boolean") setTrialEnabled(data.enabled);
        if (data.report) setTrialReport(data.report);
      })
      .catch((err) => console.error("Failed to load trial funnel status:", err))
      .finally(() => setTrialLoading(false));
  }, []);

  const saveTrialEnabled = async (next: boolean) => {
    setTrialSaving(true);
    setTrialMsg(null);
    try {
      const data = await setTrialFunnelEnabled(next);
      if (data && data.enabled === next) {
        setTrialEnabled(next);
        if (data.report) setTrialReport(data.report);
        setTrialMsg({ ok: true, text: next ? "✅ 試用漏斗已開啟——領取入口＋定時任務即時生效" : "⏸️ 試用漏斗已關閉——新領取即時擋下（已有試用中的名額照跑至到期）" });
      } else {
        setTrialMsg({ ok: false, text: "❌ 更新失敗" });
      }
    } catch (err) {
      setTrialMsg({ ok: false, text: `❌ 儲存失敗：${err instanceof Error ? err.message : "未知錯誤"}` });
    } finally {
      setTrialSaving(false);
    }
  };

  const saveAbuse = async () => {
    setAbuseSaving(true);
    setAbuseMsg(null);
    try {
      const data = await updateAntiAbuseConfig({
        lite: Number(abuseForm.lite),
        pro: Number(abuseForm.pro),
        resetMin: Number(abuseForm.resetMin),
        burstLite: Number(abuseForm.burstLite),
        burstPro: Number(abuseForm.burstPro),
        burstWindowMin: Number(abuseForm.burstWindowMin),
        burstEnabled: Boolean(abuseForm.burstEnabled),
      });
      if (data.success) {
        setAbuseMsg({ ok: true, text: "✅ 已儲存——下一個新對話 session 即時生效" });
      } else {
        setAbuseMsg({ ok: false, text: `❌ ${data.error || "更新失敗"}` });
      }
    } catch (err) {
      setAbuseMsg({ ok: false, text: `❌ 儲存失敗：${err instanceof Error ? err.message : "未知錯誤"}` });
    } finally {
      setAbuseSaving(false);
    }
  };

  const saveBrand = async () => {
    setBrandSaving(true);
    setBrandMsg(null);
    try {
      await setPlatformConfig("brand_footer", brandValue.trim());
      setBrandMsg({ ok: true, text: "✅ 已儲存——新署名約 1 分鐘內生效（對話 DO 快取）" });
    } catch (err) {
      setBrandMsg({ ok: false, text: `❌ 儲存失敗：${err instanceof Error ? err.message : "未知錯誤"}` });
    } finally {
      setBrandSaving(false);
    }
  };


  return (
  <div>
      <h1 className="text-2xl font-bold text-slate-100 mb-6">⚙️ 系統資訊</h1>

      {/* ── 分頁導航（三組）── */}
      <div className="space-y-2 mb-6">
        {TAB_GROUPS.map((g) => (
          <div key={g.group} className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-14 shrink-0">
              {g.group}
            </span>
            {g.tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  tab === t.id
                    ? "bg-emerald-600/20 border-emerald-500 text-emerald-300"
                    : "bg-slate-950/40 border-slate-700/50 text-slate-400 hover:border-slate-500 hover:text-slate-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 -mt-4 mb-6">
        {DIAGRAM_TABS.find((t) => t.id === tab)?.hint}
      </p>

      <DiagramContent tab={tab} llmKeys={llmKeys} llmKeysLoading={llmKeysLoading} />

      {tab === "employees" && <SystemInfoEmployees />}
      {tab === "trialwalk" && <TrialWalkthrough />}
      {tab === "corekb" && <CoreKbPanel />}

      <SettingsTabs
        tab={tab}
        brandValue={brandValue}
        setBrandValue={setBrandValue}
        brandLoading={brandLoading}
        brandSaving={brandSaving}
        brandMsg={brandMsg}
        saveBrand={saveBrand}
        abuseForm={abuseForm}
        setAbuseForm={setAbuseForm}
        abuseLoading={abuseLoading}
        abuseSaving={abuseSaving}
        abuseMsg={abuseMsg}
        saveAbuse={saveAbuse}
        trialEnabled={trialEnabled}
        trialReport={trialReport}
        trialLoading={trialLoading}
        trialSaving={trialSaving}
        trialMsg={trialMsg}
        saveTrialEnabled={saveTrialEnabled}
      />
    </div>
  );
}
